/* =====================================================================
 * SharedSkillsTab — 마켓플레이스 "공유 스킬" 탭
 *
 * 사내 사용자가 공유한 스킬을 테넌트 전체에 보여 준다. 카드는 내 스킬 /
 * 레퍼런스 카드와 같은 골격(`skillCards.tsx`)을 쓰되, 작성자 자리에
 * "부서 · 이름" 을 놓고 아바타는 이니셜만 그린다(사진은 내려오지 않는다).
 * 적용 동작은 레퍼런스 탭과 같다 — 활성 스킬 하나를 토글하고 입력창
 * 모드를 skill/auto 로 맞춘다. 내용은 읽기 전용 소스 패널로 본다.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Info, Search, Users, X } from 'lucide-react';
import { AccountAvatar, Button, Chip, Tooltip } from '@auw/workspace-ui';
import { useReboModeStore, useSkillStore } from '@auw/stores';
import type { SharedSkillSummary } from '@auw/api-client';
import type { SkillSourceTarget } from './SkillSourcePanel';
import { SkillPager, usePagedList } from './skillPagination';
import {
  CardGrid,
  DetailBtn,
  ErrorBanner,
  RefCardBody,
  RefCardFooter,
  RefCardHeader,
  RefCardNew,
  RefCatLabel,
  RefCreator,
  RefDesc,
  RefEmptyWrap,
  RefFooterActions,
  RefHeaderText,
  RefLoadingWrap,
  RefSearchClear,
  RefSearchInput,
  RefSearchWrap,
  RefSkillName,
  RefSpinner,
  ScrollArea,
  isZipSource,
} from './skillCards';

export interface SharedSkillsTabProps {
  onOpenSource: (target: SkillSourceTarget) => void;
  activeSkillIds: string[];
  onToggle: (id: string) => void;
  /** 카드의 "적용하기" 버튼. 마켓플레이스 페이지는 끈다. */
  showApply?: boolean;
  /** 한 페이지의 카드 수. 없으면 전부 그린다. */
  pageSize?: number;
}

/** 검색은 이름·설명·본문 미리보기·업로더 이름·부서를 모두 본다. */
function matchesQuery(skill: SharedSkillSummary, needle: string): boolean {
  return [skill.name, skill.description, skill.body_preview, skill.owner_name, skill.owner_department]
    .some((value) => !!value && value.toLowerCase().includes(needle));
}

export const SharedSkillsTab: React.FC<SharedSkillsTabProps> = ({
  onOpenSource, activeSkillIds, onToggle, showApply = true, pageSize,
}) => {
  const { t } = useTranslation('common');
  const { sharedSkills, isSharedLoading, sharedError, fetchSharedSkills } = useSkillStore();
  const { setMode } = useReboModeStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    void fetchSharedSkills();
  }, [fetchSharedSkills]);

  const visibleSkills = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return q ? sharedSkills.filter((skill) => matchesQuery(skill, q)) : sharedSkills;
  }, [sharedSkills, searchQuery]);

  const handleApply = useCallback((skillId: string) => {
    const willActivate = !activeSkillIds.includes(skillId);
    onToggle(skillId);
    setMode(willActivate ? 'skill' : 'auto');
  }, [activeSkillIds, onToggle, setMode]);

  /* 검색어가 바뀌면 1페이지로. */
  const paged = usePagedList(visibleSkills, pageSize, searchQuery);

  return (
    <TabWrap>
      <RefSearchWrap>
        <Search size={14} />
        <RefSearchInput
          type="text"
          placeholder={t('skillCreate.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <RefSearchClear
            type="button"
            onClick={() => setSearchQuery('')}
            aria-label={t('translation.clear', { defaultValue: '입력 지우기' })}
          >
            <X size={12} />
          </RefSearchClear>
        )}
      </RefSearchWrap>

      <ScrollArea>
        {/* 실패·로딩·빈 목록·검색 무결과를 하나씩만 보인다 — 목록을 못 받았을 때
         * "공유된 스킬이 없다" 고 단정하지 않고 다시 시도 버튼을 낸다. */}
        {sharedError ? (
          <ErrorBanner role="alert">
            <AlertCircle size={14} />
            {sharedError}
            <Button type="button" size="sm" variant="surface" onClick={() => { void fetchSharedSkills(); }}>
              {t('skill.source.retry')}
            </Button>
          </ErrorBanner>
        ) : isSharedLoading ? (
          <RefLoadingWrap>
            <RefSpinner size={18} />
          </RefLoadingWrap>
        ) : sharedSkills.length === 0 ? (
          <RefEmptyWrap>
            <Users size={24} />
            <span>{t('skill.sharedEmpty')}</span>
          </RefEmptyWrap>
        ) : visibleSkills.length === 0 ? (
          <RefEmptyWrap>
            <Search size={24} />
            <span>{t('search.noResults')}</span>
          </RefEmptyWrap>
        ) : (
          <CardGrid>
            {paged.pageItems.map((skill) => {
              const isActive = activeSkillIds.includes(skill.skill_id);
              const creator = [skill.owner_department, skill.owner_name || t('skill.unknownUser')]
                .filter(Boolean)
                .join(' · ');
              return (
                <RefCardNew key={skill.skill_id} $active={showApply && isActive}>
                  <RefCardHeader>
                    <AccountAvatar size={36} name={skill.owner_name ?? undefined} />
                    <RefHeaderText>
                      <RefSkillName title={skill.name}>{skill.name}</RefSkillName>
                      <RefCreator title={creator}>{creator}</RefCreator>
                    </RefHeaderText>
                  </RefCardHeader>

                  <RefCardBody>
                    <RefDesc>{skill.body_preview || skill.description || t('skill.noDescription')}</RefDesc>
                    <RefCatLabel>
                      {skill.is_mine && <Chip size="sm" variant="subtle">{t('skill.tabMy')}</Chip>}
                      {skill.has_references && <Chip size="sm" variant="subtle">references</Chip>}
                      <Chip size="sm" variant="subtle">{isZipSource(skill.source_filename) ? '.zip' : '.md'}</Chip>
                    </RefCatLabel>
                  </RefCardBody>

                  <RefCardFooter>
                    {showApply && (
                      <Button
                        type="button"
                        size="sm"
                        variant="surface"
                        aria-pressed={isActive}
                        onClick={() => handleApply(skill.skill_id)}
                      >
                        {isActive ? t('skill.deactivate') : t('skill.apply')}
                      </Button>
                    )}
                    <RefFooterActions>
                      <Tooltip label={t('skill.detail', 'Detail')}>
                        <DetailBtn
                          type="button"
                          onClick={() => onOpenSource({ id: skill.skill_id, name: skill.name, kind: 'shared' })}
                          aria-label={t('skill.detail', 'Detail')}
                        >
                          <Info size={16} />
                        </DetailBtn>
                      </Tooltip>
                    </RefFooterActions>
                  </RefCardFooter>
                </RefCardNew>
              );
            })}
          </CardGrid>
        )}
        {!sharedError && !isSharedLoading && (
          <SkillPager page={paged.page} pageCount={paged.pageCount} onChange={paged.setPage} />
        )}
      </ScrollArea>
    </TabWrap>
  );
};

export default SharedSkillsTab;

/* 레퍼런스 탭의 RefTabWrap 과 같은 골격. 카테고리 칩 줄이 없으므로 그 자리의
 * 위 여백(16px)만 검색창 위에 남긴다. */
const TabWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-top: 16px;
`;
